import { Component, OnInit } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CompanyService } from '../../../../core/services/company.service';
import { ICompany } from '../../../../core/models/ICompany';
import { categories } from '../../../utils/select-options';
import { NotificationService } from '../../../services/notification.service';
import { AddressAutocompleteService, AddressSuggestion } from '../../../services/address-autocomplete.service';
import { debounceTime, switchMap } from 'rxjs/operators';
import { AddressPipe } from '../../../pipe/address.pipe';
import { TvaService } from '../../../services/tva.service';
import { LogService } from '../../../../core/services/log.service';

@Component({
  selector: 'app-company-edit',
  imports: [
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    AddressPipe
  ],
  templateUrl: './company-edit.html',
  styleUrl: './company-edit.scss'
})
export class CompanyEdit {

  company!: ICompany;
  companyEdit = {
    companyName: '',
    companyNumber: '',
    companyEmail: '',
    companyPhoneNumber: '',
    address: { street: '', number: 0, zipCode: 0, city: '', country: '' },
    logo: '',
    category: '',
    description: '',
    socialMediaLinks: { facebook: '', instagram: '', linkedin: '' },
    gallery: [''],
  };
  apiUrl = environment.apiUrl;
  categories = categories.sort();
  addressCtrl = new FormControl('');
  suggestions: any[] = [];


  constructor(
    private companyService: CompanyService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private addressAutocompleteService: AddressAutocompleteService,
    private tvaService: TvaService,
    private logger: LogService
  ) {
    const company = this.router.currentNavigation()?.extras.state?.['company'];
    if (company) {
      this.company = company;
      this.fillCompanyEdit(company);
    } else {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) this.loadCompanyFromBackend(id);
    }
  }

  ngOnInit(): void {
    this.addressCtrl.valueChanges
      .pipe(
        debounceTime(300),
        switchMap(value => this.addressAutocompleteService.getSuggestions(value || ""))
      )
      .subscribe(results => this.suggestions = results);
  }

  onLogoSelected(event: any) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      this.companyService.uploadLogo(this.company._id, file).subscribe(logoUrl => {
        console.log(logoUrl);
        if (this.company) {
          this.company.logo = logoUrl;
        }
      });
    }
  }

  onGalleryImageSelected(event: any) {
    const file = event.target.files[0];
    if (file && this.company._id) {
      const formData = new FormData();
      formData.append('image', file);

      this.companyService.addGalleryImage(this.company._id, formData).subscribe((updatedGallery) => {
        this.companyEdit.gallery = updatedGallery;
        this.company.gallery = updatedGallery;
      });
    }
  }

  removeImage(index: number) {
    if (this.companyEdit?.gallery && this.company._id) {
      const imageToRemove = this.companyEdit.gallery[index];

      this.companyService.removeGalleryImage(this.company._id, imageToRemove)
        .subscribe((updatedGallery) => {
          this.companyEdit.gallery = updatedGallery;
          this.company.gallery = updatedGallery;
        });
    }
  }

  onSubmit() {
    this.tvaService.checkTva(this.companyEdit.companyNumber).subscribe({
      next: (result) => {
        this.logger.log("Résultat de la vérification du numéro de TVA", result);
        if (result?.isValid === false) {
          this.notificationService.error(result.message ? result.message : 'TVA invalide');
          return;
        }
        this.companyService.editCompany(this.company._id, this.companyEdit).subscribe(updatedCompany => {
          this.company = updatedCompany;
          this.notificationService.success("Société mise à jour avec succès!");
          this.router.navigate(['profile']);
        });
      },
      error: (e) => {
        this.logger.error("Erreur lors de la vérification du numéro de TVA", e);
        this.notificationService.error("Impossible de vérifier le numéro de TVA")
      }
    });
  }

  private fillCompanyEdit(company: ICompany) {
    this.companyEdit = {
      companyName: company.companyName,
      companyNumber: company.companyNumber,
      companyEmail: company.companyEmail,
      companyPhoneNumber: company.companyPhoneNumber,
      address: {
        street: company.address.street,
        number: company.address.number,
        zipCode: company.address.zipCode,
        city: company.address.city,
        country: company.address.country
      },
      logo: company.logo,
      category: company.category,
      description: company.description,
      socialMediaLinks: {
        facebook: company.socialMediaLinks?.facebook,
        instagram: company.socialMediaLinks?.instagram,
        linkedin: company.socialMediaLinks?.linkedin
      },
      gallery: [...company.gallery]
    };
  }

  private loadCompanyFromBackend(id: string) {
    this.companyService.getCompanyById(id).subscribe(c => {
      this.company = c;
      this.fillCompanyEdit(c);
    });
  }

  selectSuggestion(s: AddressSuggestion) {
    // Extraire les morceaux utiles
    const street = s.text;
    const context = s.context || [];
    const city = context.find(c => c.id.startsWith('place'))?.text || '';
    const postcode = context.find(c => c.id.startsWith('postcode'))?.text || '';
    const country = context.find(c => c.id.startsWith('country'))?.text || '';

    // Met à jour le champ visible
    this.addressCtrl.setValue(s.place_name, { emitEvent: false });

    this.companyEdit.address = {
      street,
      number: s.address ? parseInt(s.address) : 0,
      zipCode: postcode ? parseInt(postcode, 10) : 0,
      city,
      country
    };
    
    // Stocke aussi GeoJSON pour MongoDB
    (this.companyEdit as any).location = s.geometry;

    // Vider les suggestions
    this.suggestions = [];
  }
}
